import { format } from "date-fns";
import { currentTime } from "./current-time";

/**
 * Date/time display policy (repo-wide, single source of truth):
 *
 * - **UK order**: day before month (`14 Sep 2026`, never `Sep 14, 2026`).
 * - **24-hour clock**: `14:03`, never `2:03 PM`.
 * - **Fixed to Europe/London**, regardless of the viewer's device/browser
 *   timezone or the CI runner's default TZ. This is a UK-only business
 *   (Pollards, Exeter/Buckingham) and the API always sends UTC ISO
 *   timestamps — "the time" on screen should mean Europe/London time, not
 *   wherever a device happens to be set. BST/GMT is handled automatically
 *   by the IANA `Europe/London` zone.
 *
 * All formatters below accept an ISO timestamp string, a `Date`, or
 * null/undefined (returning `"-"` for the latter two so call sites can
 * pass a possibly-absent field straight through).
 *
 * Month names use a fixed English abbreviation table rather than
 * `Intl`'s locale data directly: recent ICU data renders en-GB's short
 * September as "Sept" (not "Sep"), which would silently drift from the
 * convention used elsewhere (date-fns's `MMM` token, and every existing
 * hand-written label in this codebase). `SHORT_MONTHS`/`LONG_MONTHS` are
 * exported so any bespoke month-only label elsewhere in the codebase can
 * use the same fixed table directly (indexed 0 = January) instead of
 * reaching for `Intl`/`toLocaleString` and silently reintroducing "Sept".
 */
const LONDON_TIME_ZONE = "Europe/London";
export const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;
export const LONG_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

type DateInput = string | Date | null | undefined;

function toValidDate(value: DateInput): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// `Intl.DateTimeFormat` construction compiles locale/timezone data and is
// measurably expensive to redo on every call — the options here never vary,
// so build it once and reuse it. A shared instance's `formatToParts` is
// stateless/safe to call repeatedly.
const LONDON_PARTS_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  timeZone: LONDON_TIME_ZONE,
  // Ask for the long weekday and derive the short form from it (first
  // three letters — reliable for every English day name) rather than
  // requesting both separately: one Intl call, one source of truth, and
  // it sidesteps ICU ever choosing a non-3-letter abbreviation.
  weekday: "long",
  day: "numeric",
  month: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** The wall-clock fields of `date` as seen in Europe/London. */
function londonParts(date: Date) {
  const parts = LONDON_PARTS_FORMATTER.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const monthIndex = Number(get("month")) - 1;
  const longWeekday = get("weekday");
  return {
    weekday: longWeekday.slice(0, 3),
    longWeekday,
    // en-GB's numeric-date skeleton zero-pads "day" even when asked for
    // "numeric" rather than "2-digit" (a real Intl/ICU quirk, not a typo)
    // — strip it so "1 Aug" doesn't render as "01 Aug".
    day: get("day").replace(/^0/, ""),
    shortMonth: SHORT_MONTHS[monthIndex] ?? "",
    longMonth: LONG_MONTHS[monthIndex] ?? "",
    year: get("year"),
    hour: get("hour"),
    minute: get("minute"),
  };
}

/** Format as "14 Sep 2026" (Europe/London calendar day), or "-" for absent/invalid values. */
export function formatDateOrDash(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return "-";
  const p = londonParts(date);
  return `${p.day} ${p.shortMonth} ${p.year}`;
}

/**
 * Format as "14 Sep 2026, 14:03" (date + 24-hour Europe/London time), or
 * "-" for absent/invalid values. Use this instead of `formatDateOrDash`
 * for timestamps a user actually set a specific time on (due dates,
 * reminders) — dropping the time silently loses that intent.
 */
export function formatDateTimeOrDash(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return "-";
  const p = londonParts(date);
  return `${p.day} ${p.shortMonth} ${p.year}, ${p.hour}:${p.minute}`;
}

/** Format as "14 Sep" (no year) — for chips/lists where the year is implied, or "-" for absent/invalid values. */
export function formatShortDateOrDash(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return "-";
  const p = londonParts(date);
  return `${p.day} ${p.shortMonth}`;
}

/** Format as "14 September 2026" (long month) — for prose/detail surfaces, or "-" for absent/invalid values. */
export function formatLongDateOrDash(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return "-";
  const p = londonParts(date);
  return `${p.day} ${p.longMonth} ${p.year}`;
}

/**
 * Format as "Mon" (short weekday only, Europe/London), or "-" for
 * absent/invalid values. Which weekday an instant falls on is itself
 * timezone-dependent near a day boundary — always derive it from
 * Europe/London (never a bare date-fns `format(date, "EEE")`, which uses
 * the runtime's local timezone) so it never disagrees with a
 * Europe/London clock time shown alongside it.
 */
export function formatWeekdayOrDash(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return "-";
  const p = londonParts(date);
  return p.weekday;
}

/** Format as "Mon 14 Sep" (short weekday, no year) — for rails/timelines spanning several days, or "-" for absent/invalid values. */
export function formatWeekdayDateOrDash(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return "-";
  const p = londonParts(date);
  return `${p.weekday} ${p.day} ${p.shortMonth}`;
}

/**
 * Format as "Monday 14 September" (full weekday, full month, no year) — for
 * prose day headings where the original copy was already this verbose
 * (e.g. a day-grouped log heading). Order/timezone/24-hour-clock fix only;
 * deliberately not downgraded to the short forms, which would read as a
 * quieter, less prominent heading than before. "-" for absent/invalid values.
 */
export function formatLongWeekdayDateOrDash(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return "-";
  const p = londonParts(date);
  return `${p.longWeekday} ${p.day} ${p.longMonth}`;
}

/**
 * Format as "Monday 14 September 2026" (full weekday, full month, year) —
 * the `formatLongWeekdayDateOrDash` shape plus the year, for prose where the
 * original copy showed all four (e.g. a meeting's "When" line). "-" for
 * absent/invalid values.
 */
export function formatLongWeekdayDateWithYearOrDash(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return "-";
  const p = londonParts(date);
  return `${p.longWeekday} ${p.day} ${p.longMonth} ${p.year}`;
}

/**
 * Format as "14 September" (day, full month, no year) — for prose where the
 * original copy was already this verbose. Order/timezone fix only; "-" for
 * absent/invalid values.
 */
export function formatDayLongMonthOrDash(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return "-";
  const p = londonParts(date);
  return `${p.day} ${p.longMonth}`;
}

/** Format as "14:03" (24-hour Europe/London time only), or "-" for absent/invalid values. */
export function formatTimeOrDash(value: DateInput): string {
  const date = toValidDate(value);
  if (!date) return "-";
  const p = londonParts(date);
  return `${p.hour}:${p.minute}`;
}

const SHORT_WEEKDAYS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

function parseCalendarDate(
  value: string | null | undefined,
): { year: number; month: number; day: number } | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // Bounds-check month/day so a malformed value (e.g. "2026-99-01") can't
  // index SHORT_MONTHS/SHORT_WEEKDAYS out of range and render "undefined".
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/**
 * Format a bare "YYYY-MM-DD" calendar-date value — a specific day with no
 * time-of-day or instant attached (a chart axis date, a "date" field with
 * no time component) — as "Mon 14 Sep". Reads the digits directly; never
 * constructs a `Date` and reformats it through a timezone.
 *
 * This exists because of a real bug: appending a time-of-day to a
 * date-only value before formatting it — `` `${date}T00:00:00` `` — has no
 * timezone designator, so `Date` parses it as the *runtime's local* time,
 * and reformatting that instant in Europe/London can then shift the shown
 * day by one for a viewer whose device isn't set to the UK (or whose CI
 * runner isn't). A calendar date has no instant to convert — this reads
 * the year/month/day digits and never touches `Date`'s timezone handling
 * at all, so it is correct regardless of the caller's timezone.
 *
 * Never call this on a real timestamp (something with a meaningful
 * time-of-day) — use `formatWeekdayDateOrDash` for those.
 */
export function formatCalendarWeekdayDateOrDash(
  value: string | null | undefined,
): string {
  const parts = parseCalendarDate(value);
  if (!parts) return "-";
  // Date.UTC + getUTCDay is safe here: both ends are UTC, so this is pure
  // calendar arithmetic on the digits we just parsed, never a conversion.
  const weekdayIndex = new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day),
  ).getUTCDay();
  return `${SHORT_WEEKDAYS[weekdayIndex]} ${parts.day} ${SHORT_MONTHS[parts.month - 1]}`;
}

/**
 * Format a "yyyy-MM-ddTHH:mm" wall-clock string — exactly the shape
 * `toDatetimeInputValue` produces and `<input type="datetime-local">`
 * consumes/emits — as "14 Sep 2026, 14:03". Reads the digits directly;
 * never constructs a `Date` and reformats it through Europe/London.
 *
 * A `datetime-local` input is, by the HTML spec, always the *viewer's own
 * device* wall-clock — there is no way to pin it to Europe/London. Once a
 * value is in that shape, re-parsing it with `new Date(...)` and
 * reformatting via the Europe/London-pinned formatters above
 * double-converts it: for a viewer whose device isn't set to the UK, the
 * read-only view of a due date/time would disagree with what the edit
 * input shows for the exact same value (view mode pinned to Europe/London,
 * edit mode showing the device's own local time — a real bug this fixes).
 * Use this for any prose display of a value that came from (or is going
 * straight back into) a `datetime-local` input, so the two always agree —
 * "one conversion" for both.
 */
export function formatWallClockOrDash(
  value: string | null | undefined,
): string {
  if (!value) return "-";
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return "-";
  const [, year, month, day, hour, minute] = match;
  const monthIndex = Number(month) - 1;
  // Bounds-check so a malformed value (e.g. "2026-99-01T10:30") can't index
  // SHORT_MONTHS out of range and render "undefined" instead of failing loudly.
  if (monthIndex < 0 || monthIndex > 11) return "-";
  return `${Number(day)} ${SHORT_MONTHS[monthIndex]} ${year}, ${hour}:${minute}`;
}

/**
 * Convert an ISO timestamp string to the value shape an
 * `<input type="datetime-local">` expects ("yyyy-MM-ddTHH:mm", no seconds,
 * no timezone offset — the input is always local wall-clock). Returns ""
 * for null/undefined/unparseable values, which the input renders as empty.
 */
export function toDatetimeInputValue(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

/**
 * Format a numeric GBP value as `£1,234` (or `£1,234.56` with `dp = 2`).
 * Defaults to 0 decimal places for summary surfaces; pass `dp: 2` on line-item detail pages.
 */
export function formatCurrencyGbp(value: number, dp = 0): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  }).format(value);
}

/**
 * A price quoted to a customer, rendered from the pence it is stored in.
 *
 * Deliberately not `formatCurrencyGbp`. That one rounds to whole pounds by
 * default — right for a dashboard rollup, wrong for a quote. It turned a stored
 * `16950` into "£170" on both the item page and the customer's own estimate
 * page while the email said "£169.50": two customer-facing surfaces quoting
 * different prices for the same job. The bug was invisible until a price-group
 * reseed moved rounding from £1 to 50p, so assume it had been shipping.
 *
 * Two properties do the work:
 * - **Always two decimals**, so this renders byte-identically to the email,
 *   which formats the same pence with `:.2f`. A quote that agrees with itself
 *   matters more than a tidy "£170".
 * - **Takes pence**, the unit the price is stored in. No call site divides by
 *   100, and no call site can pass pounds by mistake — the previous shape,
 *   `formatCurrencyGbp(pricePence / 100)`, made both easy.
 */
export function formatQuotedPrice(pence: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(pence / 100);
}

/**
 * Format a numeric GBP value using compact notation (`£1.2M`) for values >= 1000,
 * falling back to the plain 0-dp currency format for smaller values.
 */
export function formatCurrencyGbpCompact(value: number): string {
  if (Math.abs(value) >= 1000) {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }
  return formatCurrencyGbp(value);
}

/**
 * Headline £ on a million-scale tile, to three decimals (`£1.123m`) so the
 * thousands stay readable where the compact one-decimal form would hide them.
 */
export function formatCurrencyGbpMillions(value: number): string {
  const sign = value < 0 ? "-" : "";
  return `${sign}£${(Math.abs(value) / 1_000_000).toFixed(3)}m`;
}

/** Format an integer count with en-GB thousands separators (e.g. 1,234). */
export function formatCount(value: number): string {
  return value.toLocaleString("en-GB");
}

/**
 * The job-type label shown next to a customer order line: "Job <no>" when
 * there is a job number, "Call-off" when the backend has decided this line
 * is call-off stock (`is_call_off` — tharstern_products.is_call_off_item),
 * or no label at all otherwise.
 *
 * Deliberately not `jobNo ? "Job ..." : "Call-off"` — an order line can
 * have neither a job number nor be a call-off (e.g. a stock/express-request
 * line with no matching call-off product), and labelling it "Call-off" in
 * that case is actively misleading (staging bug: order line
 * source_order_line_id 12692, Brad 2026-09-30).
 */
export function formatJobTypeLabel(
  jobNo: string | null | undefined,
  isCallOff: boolean,
): string | null {
  if (jobNo) return `Job ${jobNo}`;
  if (isCallOff) return "Call-off";
  return null;
}

/**
 * Render a future escalation instant (department-ticket-routing's
 * `escalates_at`) as "escalates in Xh" — the "escalates in Xh" affordance
 * that surfaces escalation risk *before* it fires (product brief §11: the
 * cheapest way to make escalation rare). Pure time arithmetic, never
 * business logic — the threshold itself (which working day, which hour) is
 * computed server-side and handed over as `escalatesAt`; this only renders
 * the gap between then and `now`. `now` is test-injectable (defaults to the
 * real clock) per the repo's runtime-computed-values convention.
 *
 * Bucketing (each bucket rounds to the nearest whole unit, minimum 1):
 * - At or past `escalatesAt`: "escalation overdue" — the sweep runs
 *   hourly, so a ticket can sit briefly past its threshold before the
 *   sweep reaches it.
 * - Under a minute away: "escalates any moment"
 * - Under an hour away: "escalates in Xm"
 * - Under a day away: "escalates in Xh"
 * - A day or more away: "escalates in Xd"
 */
export function formatEscalatesIn(
  escalatesAt: string,
  now: Date = currentTime(),
): string {
  const diffMs = new Date(escalatesAt).getTime() - now.getTime();
  if (diffMs <= 0) return "escalation overdue";
  const totalMinutes = Math.floor(diffMs / 60_000);
  if (totalMinutes < 1) return "escalates any moment";
  if (totalMinutes < 60) return `escalates in ${totalMinutes}m`;
  const totalHours = Math.round(totalMinutes / 60);
  if (totalHours < 24) return `escalates in ${totalHours}h`;
  const totalDays = Math.round(totalHours / 24);
  return `escalates in ${totalDays}d`;
}

/**
 * Format a byte count for display next to a file name (e.g. `242 KB`, `1.4 MB`).
 *
 * Uses decimal (1000-based) units, matching what operating systems show for
 * file sizes, so a document's stated size agrees with what the user sees after
 * downloading it. Sub-kilobyte sizes read as whole bytes; larger units get one
 * decimal place only when it is informative.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1000) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1000;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${rounded.toLocaleString("en-GB")} ${units[unit]}`;
}
